import React, { useRef, forwardRef, LegacyRef } from 'react';
import { EntryFormProps, entryFormOptions, useAutoFocus } from '.';
import {Button, Checkbox, Col, Divider, Form, Input, List, message, Row, Tooltip} from 'antd';
import {FormItemClassNames, formItemLayout} from 'common/form';
import {FormComponentProps, FormItemProps} from 'antd/lib/form';
import OrgNotifEmailListEditor, { validateOrgNotifEmailListEditor } from 'components/org-notif-email-list-editor';
import useUserGroups from 'hooks/useUserGroups';
import {useVeriflytIdState} from "../../compliance/veriflyt-id-input/veriflyt-id-input";
import VeriflytIdInput from "../../compliance/veriflyt-id-input";
import gql from "graphql-tag";
import {VeriflytAccountFragment} from "../../../Queries/Veriflyt";
import {useQuery} from "react-apollo";
import {VeriflytAccountFragmentFragment} from "../../../__generated__/graphql/types";

export interface EmployerEntryFormProps extends EntryFormProps {

}

const VERIFLYT_ACCOUNTS_QUERY = gql`
    query EmployerEntryForm_VeriflytAccounts($employerVerID: ID!, $contractorVerID: ID!){
        employerAccount: GetVeriflytAccount(veriflytID: $employerVerID){
            ...VeriflytAccountFragment
        }
        contractorAccount: GetVeriflytAccount(veriflytID: $contractorVerID){
            ...VeriflytAccountFragment
        }
    }
    ${VeriflytAccountFragment}
`

const useGetVeriflytAccountsQuery = (baseVeriflytID: string) => {
    // Filter out any letters from baseVeriflytID to prevent double prefixes
    const numericVeriflytID = baseVeriflytID ? baseVeriflytID.replace(/[A-Za-z]/g, '') : '';
    const employerVerID = 'B' + numericVeriflytID;
    const contractorVerID = 'C' + numericVeriflytID;

    const { data, ...rest } = useQuery(VERIFLYT_ACCOUNTS_QUERY, {
        variables: {
            employerVerID,
            contractorVerID
        },
        skip: !baseVeriflytID,
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all'
    });

    return {
        ...rest,
        employerAccount: data?.employerAccount as VeriflytAccountFragmentFragment,
        contractorAccount: data?.contractorAccount as VeriflytAccountFragmentFragment
    }
}

const checkboxLayout: FormItemProps = {
    labelCol: {
        style: {
            flex: 1,
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center'
        }
    },
    wrapperCol: {
        style: {
            width: 'fit-content'
        }
    },
    style: {
        width: '100%',
        display: 'flex'
    }
};

const EmployerEntryForm: React.FC<EmployerEntryFormProps> = forwardRef((props, ref: LegacyRef<Form>) => {
    const [ userGroups ] = useUserGroups();
    const nameInputRef = useRef(null);
    const { employerAccount, contractorAccount } = useGetVeriflytAccountsQuery(
        props.form.getFieldValue('veriflytID')
    );

    const { getFieldDecorator, getFieldValue, setFieldsValue } = props.form;

    useAutoFocus(props.autoFocus, nameInputRef.current);

    let veriflytIDs: React.ReactNode[] = [];

    if (employerAccount){
        veriflytIDs.push(
            <List.Item key="employerVeriflytID">
                <List.Item.Meta title="Employer Veriflyt ID" />
                <strong>{employerAccount.veriflytID}</strong>
                <Tooltip title="Copy to clipboard">
                    <Button
                        size="small"
                        icon="copy"
                        onClick={() => {
                            navigator.clipboard.writeText(employerAccount.veriflytID || '');
                            message.success(`'${employerAccount.veriflytID}' copied to clipboard`);
                        }}
                        style={{ marginLeft: 12 }}
                    />
                </Tooltip>
            </List.Item>
        )
    }

    if (contractorAccount){
        veriflytIDs.push(
            <List.Item key="contractorVeriflytID">
                <List.Item.Meta title="Contractor Veriflyt ID" />
                <strong>{contractorAccount.veriflytID}</strong>
                <Tooltip title="Copy to clipboard">
                    <Button
                        size="small"
                        icon="copy"
                        onClick={() => {
                            navigator.clipboard.writeText(contractorAccount.veriflytID || '');
                            message.success(`'${contractorAccount.veriflytID}' copied to clipboard`);
                        }}
                        style={{ marginLeft: 12 }}
                    />
                </Tooltip>
            </List.Item>
        )
    }
    
    return <Form ref={ref}>
        <Form.Item label="Name" {...formItemLayout}>
        {getFieldDecorator('name', {
            rules: [ { required: true, message: 'Name is required' } ]
        })(<Input ref={nameInputRef} autoFocus />)}
        </Form.Item>
        {userGroups.findIndex(g =>
            g === 'flytsuite.management.emailtriggers.all' ||
            g === 'flytsuite.management.all' ||
            g === 'flytsuite.master.all'
        ) > -1 ?
            <Form.Item label="Email Triggers">
                {getFieldDecorator('notificationEmailList', {
                    rules: [{
                        validator: validateOrgNotifEmailListEditor
                    }]
                })
                (<OrgNotifEmailListEditor />)}
            </Form.Item>
        : null}
        <Divider>VeriFlyt Registration</Divider>
        {veriflytIDs.length > 0 ? <>
            <h4>Registered Veriflyt IDs</h4>
            <List bordered size="small" style={{ marginBottom: 12 }}>
                {veriflytIDs}
            </List>
        </> : null}
        <List bordered size="small">
            <List.Item
                onClick={() => !employerAccount && setFieldsValue({ registerEmployerVeriflytAccount: !getFieldValue('registerEmployerVeriflytAccount') })}
                style={{
                    cursor: !employerAccount ? 'pointer' : 'default',
                }}
            >
                <Form.Item className={FormItemClassNames.NoMargin} label="Register as Employer" {...checkboxLayout}>
                    {getFieldDecorator('registerEmployerVeriflytAccount', {
                        valuePropName: 'checked',
                        initialValue: !!employerAccount
                    })(<Checkbox disabled={!!employerAccount} />)}
                </Form.Item>
            </List.Item>
            <List.Item
                onClick={() => !contractorAccount && setFieldsValue({ registerContractorVeriflytAccount: !getFieldValue('registerContractorVeriflytAccount') })}
                style={{
                    cursor: !contractorAccount ? 'pointer' : 'default',
                }}
            >
                <Form.Item className={FormItemClassNames.NoMargin} label="Register as Contractor" {...checkboxLayout}>
                    {getFieldDecorator('registerContractorVeriflytAccount', {
                        valuePropName: 'checked',
                        initialValue: !!contractorAccount
                    })(<Checkbox disabled={!!contractorAccount} />)}
                </Form.Item>
            </List.Item>
        </List>
        {props.dupeChecker}
    </Form>
})

export default Form.create<EmployerEntryFormProps & FormComponentProps>(entryFormOptions<EmployerEntryFormProps>())(EmployerEntryForm)